export interface AssuntoStructuredOption {
  filter_value: string;
  agenda: string;
  tema?: string | null;
}

/** Ordem do slicer Power BI (espelho de mma_agenda_catalog.py). */
const MMA_AGENDA_PARENTS: string[] = [
  'AGENDA AMBIENTAL URBANA',
  'BIODIVERSIDADE',
  'COMPENSAÇÃO AMBIENTAL',
  'EDUCAÇÃO AMBIENTAL',
  'FUNDOS',
  'GESTÃO DE FLORESTAS PÚBLICAS',
  'PATRIMÔNIO E RECURSOS GENÉTICOS',
  'QUEIMADAS E COMBATE AO DESMATAMENTO',
  'SISNAMA',
  'UTILIZAÇÃO DE PRODUTOS FLORESTAIS',
  'AMAZÔNIA LEGAL',
  'BIOECONOMIA',
  'COMUNIDADES TRADICIONAIS',
  'EMERGÊNCIA AMBIENTAL',
  'GERAL',
  'JARDIM BOTÂNICO',
  'PESCA',
  'RECUPERAÇÃO DE ÁREA',
  'SMC',
  'ZONEAMENTO ECOLÓGICO ECONÔMICO',
  'ÁREAS PROTEGIDAS E UNIDADES DE CONSERVAÇÃO',
  'CLIMA',
  'CONAMA',
  'EXPLORAÇÃO DE PETRÓLEO E GÁS NATURAL',
  'GERENCIAMENTO COSTEIRO',
  'LICENCIAMENTO AMBIENTAL',
  'PLANO DE MANEJO',
  'RECURSOS HÍDRICOS',
  'TURISMO',
  'ATIVIDADES POTENCIALMENTE POLUIDORAS',
  'COLEGIADOS',
  'DESENVOLVIMENTO RURAL',
  'FISCALIZAÇÃO E INFRAÇÃO AMBIENTAL',
  'GESTÃO ADMINISTRATIVA DO ÓRGÃO',
  'PAGAMENTO POR SERVIÇOS AMBIENTAIS',
  'POLUIÇÃO MARINHA',
  'SEGURANÇA QUÍMICA',
  'USO DE AGROTÓXICOS',
];

const PARENT_ORDER = new Map(MMA_AGENDA_PARENTS.map((name, idx) => [name.toLowerCase(), idx]));

function titleAgenda(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
}

function parseRawAssunto(raw: string): { parent: string; child: string | null } {
  const parts = raw.split(' / ').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { parent: parts[0], child: parts.slice(1).join(' / ') };
  }
  if (parts.length === 1) {
    return { parent: parts[0], child: null };
  }
  return { parent: raw.trim(), child: null };
}

/** Monta opções estruturadas a partir dos valores brutos do BD (fallback sem endpoint novo). */
export function buildStructuredAssuntoOptionsFromRaw(
  rawValues: string[],
  q?: string | null,
): AssuntoStructuredOption[] {
  const byFilter = new Map<string, AssuntoStructuredOption & { agendaKey: string }>();

  for (const raw of rawValues) {
    const trimmed = (raw || '').trim();
    if (!trimmed) continue;
    const { parent, child } = parseRawAssunto(trimmed);
    byFilter.set(trimmed, {
      filter_value: trimmed,
      agenda: titleAgenda(parent),
      tema: child ? child.toLowerCase() : null,
      agendaKey: parent.toLowerCase(),
    });
  }

  let items = Array.from(byFilter.values());
  items.sort((a, b) => {
    const orderA = PARENT_ORDER.get(a.agendaKey) ?? 10_000;
    const orderB = PARENT_ORDER.get(b.agendaKey) ?? 10_000;
    if (orderA !== orderB) return orderA - orderB;
    const agendaCmp = a.agenda.localeCompare(b.agenda, 'pt-BR', { sensitivity: 'base' });
    if (agendaCmp !== 0) return agendaCmp;
    return (a.tema || '').localeCompare(b.tema || '', 'pt-BR', { sensitivity: 'base' });
  });

  if (q?.trim()) {
    const needle = q.trim().toLowerCase();
    items = items.filter(
      (item) =>
        item.filter_value.toLowerCase().includes(needle) ||
        item.agenda.toLowerCase().includes(needle) ||
        (item.tema || '').toLowerCase().includes(needle),
    );
  }

  return items.map(({ filter_value, agenda, tema }) => ({ filter_value, agenda, tema }));
}

/** Label de exibição: «Agenda (tema)» ou só agenda. */
export function formatAssuntoLabel(option: Pick<AssuntoStructuredOption, 'agenda' | 'tema'>): string {
  const agenda = (option.agenda || '').trim();
  const tema = (option.tema || '').trim();
  if (tema) {
    return `${agenda} (${tema})`;
  }
  return agenda;
}

/** Fallback quando só temos o valor bruto do BD. */
export function formatAssuntoFilterValue(filterValue: string): string {
  const raw = (filterValue || '').trim();
  if (!raw) return '';
  const parts = raw.split(' / ').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const agenda = parts[0]
      .toLowerCase()
      .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
    const tema = parts.slice(1).join(' / ').toLowerCase();
    return `${agenda} (${tema})`;
  }
  return raw
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
}

export function findAssuntoOption(
  options: AssuntoStructuredOption[],
  filterValue: string | null | undefined,
): AssuntoStructuredOption | null {
  if (!filterValue) return null;
  return options.find((o) => o.filter_value === filterValue) ?? null;
}
